import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { getValentinePage } from '../lib/supabase';
import toast from 'react-hot-toast';

interface ValentinePage {
  id: string;
  sender: string;
  receiver: string;
  message: string;
  package_type: string;
  spotify_title: string;
  spotify_artist: string;
  spotify_embed_url: string;
  image1_url: string | null;
  image2_url: string | null;
  card_message?: string | null;
}

export default function ViewValentine() {
  const { id } = useParams();
  const [page, setPage] = useState<ValentinePage | null>(null);
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handlePasswordSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!id) return;
    
    setLoading(true);
    setError(null);

    try {
      const valentinePage = await getValentinePage(id, password);
      // Ensure the data matches our ValentinePage interface
      const typedPage: ValentinePage = {
        id: valentinePage.id,
        sender: valentinePage.sender,
        receiver: valentinePage.receiver,
        message: valentinePage.message,
        package_type: valentinePage.package_type,
        spotify_title: valentinePage.spotify_title,
        spotify_artist: valentinePage.spotify_artist,
        spotify_embed_url: valentinePage.spotify_embed_url,
        image1_url: valentinePage.image1_url,
        image2_url: valentinePage.image2_url,
        card_message: valentinePage.card_message
      };
      setPage(typedPage);
      setIsAuthenticated(true);
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
        toast.error(error.message);
      } else {
        setError('Failed to load Valentine');
        toast.error('Failed to load Valentine');
      }
      setPassword('');
    } finally {
      setLoading(false);
    }
  }

  if (!isAuthenticated) {
    return (
      <div className="max-w-md mx-auto px-4 py-16">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-2xl font-bold text-pink-600 mb-6 text-center">
            Protected Valentine
          </h1>
          <p className="text-gray-600 mb-6 text-center">
            This Valentine is password protected. Please enter the password to view it.
          </p>
          {error && (
            <p className="text-red-600 text-sm text-center mb-4">
              {error}
            </p>
          )}
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50"
            >
              {loading ? 'Checking...' : 'View Valentine'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="text-center py-8">
        <h1 className="text-2xl font-bold text-gray-800">Valentine Not Found</h1>
        <p className="text-gray-600 mt-2">This Valentine page doesn't exist or hasn't been approved yet.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="p-6">
          <h1 className="text-3xl font-bold text-pink-600 mb-4">
            To: {page.receiver}
          </h1>
          <p className="text-xl text-gray-700 mb-6">{page.message}</p>
          
          <div className="grid gap-6 mb-6">
            {page.image1_url && (
              <div className="relative pt-[56.25%]">
                <img
                  src={page.image1_url}
                  alt="Valentine image 1"
                  className="absolute inset-0 w-full h-full object-cover rounded-lg shadow-md"
                />
              </div>
            )}
            {page.package_type === 'cupids_masterpiece' && page.image2_url && (
              <div className="relative pt-[56.25%]">
                <img
                  src={page.image2_url}
                  alt="Valentine image 2"
                  className="absolute inset-0 w-full h-full object-cover rounded-lg shadow-md"
                />
              </div>
            )}
          </div>

          {page.card_message && (
            <div className="bg-pink-50 p-6 rounded-lg mb-6">
              <h2 className="text-xl font-semibold text-pink-700 mb-2">Special Message</h2>
              <p className="text-pink-900">{page.card_message}</p>
            </div>
          )}

          {page.spotify_embed_url && (
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Our Song</h2>
              <p className="text-gray-600 mb-2">
                {page.spotify_title} by {page.spotify_artist}
              </p>
              <iframe
                style={{ borderRadius: '12px' }}
                src={page.spotify_embed_url}
                width="100%"
                height="352"
                frameBorder="0"
                allowFullScreen
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
              ></iframe>
            </div>
          )}

          <p className="text-right text-gray-600">
            With love, {page.sender}
          </p>
        </div>
      </div>
    </div>
  );
}